let sentryModule: typeof import("@sentry/react") | null = null;
const pendingErrors: Array<{ error: unknown; extra?: Record<string, unknown>; tags?: Record<string, string> }> = [];

const IGNORED_ERRORS = [
  "Java object is gone",
  "ResizeObserver loop",
  "ResizeObserver loop completed with undelivered notifications",
  "Non-Error promise rejection captured",
  "Minified invariant #4412",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "is not a valid JavaScript MIME type",
  "The object does not support the operation or argument",
  "InvalidAccessError",
  "Failed to fetch",
  "NetworkError",
  "Load failed",
  "The Internet connection appears to be offline",
  "Network request failed",
];

const THIRD_PARTY_URL_PATTERNS = [
  /\/embed\.js$/,
  /instagram\.com/,
  /facebook\.com/,
  /google-analytics\.com/,
  /googletagmanager\.com/,
];

export async function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn("Missing VITE_SENTRY_DSN — Sentry is disabled");
    return;
  }

  const Sentry = await import("@sentry/react");
  sentryModule = Sentry;

  Sentry.init({
    dsn,
    release: import.meta.env.VITE_APP_VERSION,
    initialScope: {
      tags: {
        deploy_timestamp: import.meta.env.VITE_DEPLOY_TIMESTAMP,
      },
    },
    integrations: import.meta.env.PROD
      ? [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ]
      : [Sentry.browserTracingIntegration()],
    // Critical UX flows (booking, payment, walk-in, NGU lead) at 100%;
    // everything else at 10% to keep trace volume manageable.
    tracesSampler: (samplingContext) => {
      const path =
        samplingContext.location?.pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "");
      if (
        /^\/(book|booking|deposit|checkout|payment|paypal|stripe|walkin|walk-in|netglowup\/(report|claim))/i.test(path)
      ) {
        return 1.0;
      }
      return 0.1;
    },
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value || "";
      if (IGNORED_ERRORS.some((pattern) => message.includes(pattern))) {
        return null;
      }

      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      const hasThirdParty = frames.some((frame) => {
        const filename = frame.filename || "";
        return THIRD_PARTY_URL_PATTERNS.some((pattern) => pattern.test(filename));
      });
      if (hasThirdParty) {
        return null;
      }

      // Scrub any bearer tokens from the request URL captured with the event
      if (event.request?.url) {
        event.request.url = sanitizeUrlForLogging(event.request.url);
      }

      return event;
    },

    // Scrub bearer-token URLs from performance transactions before they reach Sentry.
    // Without this, every page load on /artist-portal/:token, /admin/:scope/:token, etc.
    // would send the raw token as the transaction name and request.url.
    beforeSendTransaction(event) {
      if (event.transaction) {
        event.transaction = sanitizeUrlForLogging(event.transaction);
      }
      if (event.request?.url) {
        event.request.url = sanitizeUrlForLogging(event.request.url);
      }
      return event;
    },

    // Scrub bearer-token URLs from automatic navigation and fetch breadcrumbs.
    // Breadcrumbs are attached to error events and replays, so a leaked token in
    // a breadcrumb URL is as dangerous as one in a transaction name.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "navigation") {
        if (breadcrumb.data?.from && typeof breadcrumb.data.from === "string") {
          breadcrumb.data.from = sanitizeUrlForLogging(breadcrumb.data.from);
        }
        if (breadcrumb.data?.to && typeof breadcrumb.data.to === "string") {
          breadcrumb.data.to = sanitizeUrlForLogging(breadcrumb.data.to);
        }
      }
      if (
        (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") &&
        breadcrumb.data?.url &&
        typeof breadcrumb.data.url === "string"
      ) {
        breadcrumb.data.url = sanitizeUrlForLogging(breadcrumb.data.url);
      }
      return breadcrumb;
    },
  });

  if (pendingErrors.length > 0) {
    pendingErrors.forEach(({ error, extra, tags }) => {
      const hasExtra = extra && Object.keys(extra).length > 0;
      const hasTags = tags && Object.keys(tags).length > 0;
      Sentry.captureException(error, {
        ...(hasExtra ? { extra } : {}),
        ...(hasTags ? { tags } : {}),
      });
    });
    pendingErrors.length = 0;
  }
}

export function captureException(
  error: unknown,
  extra?: Record<string, unknown>,
  tags?: Record<string, string>,
) {
  if (sentryModule) {
    const hasExtra = extra && Object.keys(extra).length > 0;
    const hasTags = tags && Object.keys(tags).length > 0;
    sentryModule.captureException(error, {
      ...(hasExtra ? { extra } : {}),
      ...(hasTags ? { tags } : {}),
    });
  } else {
    pendingErrors.push({ error, extra, tags });
    // sentry-ok: Sentry module not yet initialised; error is queued and replayed on init
    console.error("[Sentry loading] error queued:", error);
  }
}

/**
 * Capture an error thrown inside a <SectionBoundary>. Tags the event with the
 * section name so dashboards can group failures by panel.
 */
export function captureSectionError(
  sectionName: string,
  error: unknown,
  extra?: Record<string, unknown>,
) {
  const merged = { section: sectionName, ...(extra ?? {}) };
  if (sentryModule) {
    sentryModule.withScope((scope) => {
      scope.setTag("section", sectionName);
      scope.setExtras(merged);
      sentryModule!.captureException(error);
    });
  } else {
    pendingErrors.push({ error, extra: merged });
    // sentry-ok: Sentry module not yet initialised; section error is queued and replayed on init
    console.error(`[Sentry loading] section error queued (${sectionName}):`, error);
  }
}

export function captureMessage(message: string, extra?: Record<string, unknown>, level: "info" | "warning" | "error" = "info") {
  if (sentryModule) {
    let safeExtra = extra;
    if (extra && typeof extra.page === "string") {
      safeExtra = { ...extra, page: sanitizeUrlForLogging(extra.page) };
    }
    sentryModule.captureMessage(message, { level, extra: safeExtra });
  }
}

/**
 * Scrub admin/manage tokens, JWT-shaped path segments, query params, and
 * fragments from a URL before it is logged to Sentry.
 *
 * Handles both relative paths (/artist-portal/…) and absolute URLs
 * (https://example.com/artist-portal/…).  For absolute inputs the scheme +
 * host are preserved in the return value so Sentry context is still useful,
 * but the scrubbed path replaces the original one.
 *
 * Scrubbing applied to the pathname (in order):
 *   1. Explicit bearer-token route prefixes → last segment replaced with :token
 *      (defense-in-depth for routes whose tokens may be shorter / non-hex)
 *   2. Long hex path segments (24+ chars of [a-f0-9]) → :token
 *      (covers all crypto.randomBytes(N).toString("hex") tokens used in this app)
 *   3. UUID-shaped segments (8-4-4-4-12 hex) → :token
 *   4. JWT-shaped segments (xxx.yyy.zzz base64url) → :jwt
 *   5. Query string and fragment are dropped entirely.
 *
 * Admin token routes (/admin/:scope/:token) are intentionally NOT listed in
 * BEARER_TOKEN_ROUTES: admin tokens are always 64-char hex strings and are
 * already caught by rule 2.  Adding a generic /admin/[^/]+/ pattern would
 * incorrectly scrub non-token admin path segments (e.g. /admin/netglowup/hub).
 * If a future admin route ever uses a non-hex token, add it explicitly here.
 *
 * Add new entries to BEARER_TOKEN_ROUTES whenever a tokenized page is
 * introduced that uses a token format NOT covered by rules 2–4.
 */

/**
 * Route prefixes where the LAST path segment is a reusable bearer token.
 * Each regex must have exactly two capture groups: (prefix)(token).
 *
 * All tokens in this app are currently 48- or 64-char hex strings (rules 2+
 * already cover them), but these explicit entries provide defense-in-depth
 * and document which routes are security-sensitive.
 */
const BEARER_TOKEN_ROUTE_PATTERNS: RegExp[] = [
  /^(\/artist-portal\/)([^/?#]+)/,
  /^(\/receptionist-portal\/)([^/?#]+)/,
  /^(\/walkin\/)([^/?#]+)/,
  /^(\/checkin\/)([^/?#]+)/,
  /^(\/healed-photo\/[^/]+\/)([^/?#]+)/,
  /^(\/my\/verify\/)([^/?#]+)/,
  /^(\/survey\/owner\/)([^/?#]+)/,
  /^(\/netglowup\/profile\/)([^/?#]+)/,
  /^(\/release-form\/[^/]+\/)([^/?#]+)/,
  /^(\/client-portal\/)([^/?#]+)/,
  /^(\/support\/)([^/?#]+)/,
];

/** Scrub a URL pathname in-place using all token-stripping rules. */
function scrubPathname(pathname: string): string {
  let p = pathname;
  // 1. Explicit bearer-token route prefixes
  for (const re of BEARER_TOKEN_ROUTE_PATTERNS) {
    p = p.replace(re, "$1:token");
  }
  // 2. Long hex-ish token path segments (crypto.randomBytes hex tokens)
  p = p.replace(/\/[a-f0-9]{24,}/gi, "/:token");
  // 3. UUID-shaped segments (8-4-4-4-12 hex)
  p = p.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "/:token");
  // 4. JWT-shaped segments (xxx.yyy.zzz base64url)
  p = p.replace(/\/[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, "/:jwt");
  return p;
}

export function sanitizeUrlForLogging(input: string): string {
  if (!input || input === "unknown") return input;

  // Detect absolute URLs (scheme://host/path).  Parse, scrub the pathname,
  // drop query + fragment, reconstruct origin + scrubbed path.
  if (/^https?:\/\//i.test(input)) {
    try {
      const parsed = new URL(input);
      const scrubbed = scrubPathname(parsed.pathname);
      return `${parsed.origin}${scrubbed}`;
    } catch {
      // Malformed absolute URL — fall through to path-only scrubbing below.
    }
  }

  // Relative path: strip query/fragment then scrub.
  const path = input.split("?")[0].split("#")[0];
  return scrubPathname(path);
}

/**
 * Call this whenever a fetch to a protected route returns 401.
 * It fires a Sentry "warning" event so auth failures surface in the dashboard
 * before an artist or admin has to report them manually.
 *
 * URL is sanitized before being logged so admin/manage tokens never reach
 * third-party telemetry.
 */
export function captureAuthFailure(url: string, context?: Record<string, unknown>) {
  const safeUrl = sanitizeUrlForLogging(url);
  const message = `[auth:401] Client fetch returned 401 — ${safeUrl}`;
  if (sentryModule) {
    sentryModule.captureMessage(message, { level: "warning", extra: context });
  } else {
    // sentry-ok: Sentry module not yet initialised; auth failure logged to console only
    console.error(message, context);
  }
}

/**
 * Add a breadcrumb to the current Sentry scope.
 * Breadcrumbs appear in the "Breadcrumbs" tab of an issue, giving timeline
 * context leading up to an error. Use this for non-fatal diagnostic events
 * (e.g. "contract validation failed but app continued").
 */
export function addBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
  level: "debug" | "info" | "warning" | "error" = "warning",
) {
  if (sentryModule) {
    sentryModule.addBreadcrumb({ message, data, level, type: "default" });
  } else {
    console.warn(`[Sentry breadcrumb] ${message}`, data);
  }
}
